import { FC, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "../lib/toast";
import { apiClient } from "../utils/api";
import { useAuth } from "../contexts/AuthContext";

interface SyncRule {
  applicant: number; // Sync rule for applicant data
  relative: number; // Sync rule for relative data
  application: number; // Sync rule for application data
}

const SyncRuleConfiguration: FC = () => {
  const navigate = useNavigate();
  const { portalId, encryptedUserId, isLoading: authLoading, error: authError } = useAuth();

  const [syncRules, setSyncRules] = useState<SyncRule>({ 
    applicant: 2, 
    relative: 2, 
    application: 2 
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const syncRuleOptions = [
    {
      value: 1,
      stringValue: "hubspot_unless_blank",
      label: "Use HubSpot unless blank",
      description: "Use HubSpot data unless the field is empty, then use Veracross data"
    },
    {
      value: 2,
      stringValue: "veracross_unless_blank",
      label: "Use Veracross unless blank", 
      description: "Use Veracross data unless the field is empty, then use HubSpot data"
    }
  ];



  useEffect(() => {
    const fetchSyncRules = async () => {
      if (authLoading) {
        return;
      }

      if (!portalId) {
        setLoading(false);
        return;
      }

      try {
        const response = await apiClient.getSyncRules(portalId, encryptedUserId || "");
        if (response.success && response.syncRules) {
          setSyncRules(response.syncRules);
        } else {
          // Use default rules if none exist
          setSyncRules({ 
            applicant: 2, 
            relative: 2, 
            application: 2 
          });
        }
      } catch (error) {
        // Use default rules on error
        setSyncRules({ 
          applicant: 2, 
          relative: 2, 
          application: 2 
        });
      } finally {
        setLoading(false);
      }
    };

    fetchSyncRules();
  }, [portalId, authLoading, authError]);

  const handleSyncRuleChange = (dataType: keyof SyncRule, newRuleValue: string) => {
    const ruleNumber = parseInt(newRuleValue, 10);
    setSyncRules(prev => ({
      ...prev,
      [dataType]: ruleNumber
    }));
  };

  const handleBack = () => {
    navigate(
      `/application-field-mapping?userId=${encodeURIComponent(encryptedUserId || "")}`
    );
  };

  const handleNext = async () => {
    if (!portalId) {
      toast({
        title: "Error",
        description: "Portal ID is missing",
        variant: "destructive",
      });
      return;
    }

    setSaving(true);
    try {
      const response = await apiClient.saveSyncRules(portalId, encryptedUserId || "", syncRules);
      
      if (response.success) {
        toast({
          title: "Sync Rules Configured",
          description: "Your synchronization rules have been saved successfully.",
        });
        navigate(`/complete?userId=${encodeURIComponent(encryptedUserId || "")}`);
      } else {
        toast({
          title: "Error",
          description: response.error || "Failed to save sync rules",
          variant: "destructive",
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to save sync rules",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  if (!portalId) {
    return (
      <div className="w-full">
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="text-center py-12">
            <p className="text-red-600 mb-4">Portal ID is missing</p>
            <p className="text-gray-500">
              Please return to the previous step and try again.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="w-full">
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="text-center py-12">
            <p className="text-gray-500">Loading sync rule configuration...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-6xl mx-auto">
      <Card>
        <CardHeader>
          <CardTitle className="text-2xl font-bold">
            Sync Rule Configuration
          </CardTitle>
          <CardDescription>
            Configure how data conflicts should be resolved during synchronization between HubSpot and Veracross.
            These rules determine which system's data takes precedence when the same field has different values.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-8">
          {/* Single UI Element with Three Sync Rules */}
          <div className="border rounded-lg p-6 space-y-6">
            <div className="text-center">
              <h3 className="text-lg font-semibold mb-4">Data Synchronization Rules</h3>
              <p className="text-gray-600 text-sm">
                Configure how data conflicts should be resolved for each data type during synchronization
              </p>
            </div>
            
            {/* Three Sync Rules in One UI */}
            <div className="space-y-6">
              {/* Applicant Data Sync Rule */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-center p-4 bg-gray-50 rounded-lg">
                <div className="text-left">
                  <div className="flex items-center space-x-2 mb-1">
                    <div className="w-6 h-6 bg-[#ff7a59] rounded flex items-center justify-center">
                      <span className="text-white font-bold text-xs">D</span>
                    </div>
                    <span className="text-xs font-medium">HubSpot Deals</span>
                  </div>
                  <p className="text-xs text-gray-600 mt-1">Applicant Data</p>
                </div>

                <div className="text-center">
                  <Select
                    value={syncRuleOptions.find(opt => opt.value === syncRules.applicant)?.label || ""}
                    onValueChange={(label) => {
                      const option = syncRuleOptions.find(opt => opt.label === label);
                      if (option) handleSyncRuleChange('applicant', option.value.toString());
                    }}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {syncRuleOptions.map((option) => (
                        <SelectItem key={option.value} value={option.label}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="text-left">
                  <div className="flex items-center space-x-2 mb-1">
                    <div className="w-6 h-6 bg-blue-600 rounded flex items-center justify-center">
                      <span className="text-white font-bold text-xs">A</span>
                    </div>
                    <span className="text-xs font-medium">Veracross Applicants</span>
                  </div>
                </div>
              </div>

              {/* Parent Data Sync Rule */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-center p-4 bg-gray-50 rounded-lg">
                <div className="text-left">
                  <div className="flex items-center space-x-2 mb-1">
                    <div className="w-6 h-6 bg-[#ff7a59] rounded flex items-center justify-center">
                      <span className="text-white font-bold text-xs">C</span>
                    </div>
                    <span className="text-xs font-medium">HubSpot Contacts</span>
                  </div>
                  <p className="text-xs text-gray-600 mt-1">Parent Data</p>
                </div>

                <div className="text-center">
                  <Select
                    value={syncRuleOptions.find(opt => opt.value === syncRules.relative)?.label || ""}
                    onValueChange={(label) => {
                      const option = syncRuleOptions.find(opt => opt.label === label);
                      if (option) handleSyncRuleChange('relative', option.value.toString());
                    }}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {syncRuleOptions.map((option) => (
                        <SelectItem key={option.value} value={option.label}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="text-left">
                  <div className="flex items-center space-x-2 mb-1">
                    <div className="w-6 h-6 bg-blue-600 rounded flex items-center justify-center">
                      <span className="text-white font-bold text-xs">P</span>
                    </div>
                    <span className="text-xs font-medium">Veracross Parents</span>
                  </div>
                </div>
              </div>

              {/* Application Data Sync Rule */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-center p-4 bg-gray-50 rounded-lg">
                <div className="text-left">
                  <div className="flex items-center space-x-2 mb-1">
                    <div className="w-6 h-6 bg-[#ff7a59] rounded flex items-center justify-center">
                      <span className="text-white font-bold text-xs">D</span>
                    </div>
                    <span className="text-xs font-medium">HubSpot Deals</span>
                  </div>
                  <p className="text-xs text-gray-600 mt-1">Application Data</p>
                </div>

                <div className="text-center">
                  <Select
                    value={syncRuleOptions.find(opt => opt.value === syncRules.application)?.label || ""}
                    onValueChange={(label) => {
                      const option = syncRuleOptions.find(opt => opt.label === label);
                      if (option) handleSyncRuleChange('application', option.value.toString());
                    }}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {syncRuleOptions.map((option) => (
                        <SelectItem key={option.value} value={option.label}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="text-left">
                  <div className="flex items-center space-x-2 mb-1">
                    <div className="w-6 h-6 bg-blue-600 rounded flex items-center justify-center">
                      <span className="text-white font-bold text-xs">D</span>
                    </div>
                    <span className="text-xs font-medium">Veracross Applications</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Information Panel */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <h4 className="font-semibold text-yellow-800 mb-2">Important Information</h4>
            <ul className="text-sm text-yellow-700 space-y-1">
              <li>• <strong>Use HubSpot unless blank:</strong> Prefer HubSpot data, but use Veracross data when HubSpot field is empty</li>
              <li>• <strong>Use Veracross unless blank:</strong> Prefer Veracross data, but use HubSpot data when Veracross field is empty</li>
              <li>• <strong>Update only:</strong> Edits apply to existing records, changes won't create a new record</li>
              <li>• <strong>Updated every 24 hours.</strong></li>
            </ul>
          </div>

          {/* Navigation Buttons */}
          <div className="flex justify-between pt-6 border-t">
            <Button variant="outline" onClick={handleBack}>
              Back
            </Button>
            <Button 
              onClick={handleNext} 
              disabled={saving}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {saving ? "Saving..." : "Complete Setup"}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SyncRuleConfiguration;
